import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusBadge from './ui/StatusBadge';
import StatusStepper from './ui/StatusStepper';
import Button from './ui/Button';
import { useConfirm } from './ui/ConfirmProvider';
import { FormInput, FormSelect, FormTextarea } from './FormField';
import { useNotifications } from './NotificationProvider';
import OrderLines from './procurement/OrderLines';
import OrderParcels from './procurement/OrderParcels';
import OrderShipments from './procurement/OrderShipments';
import LandedCostPanel from './procurement/LandedCostPanel';
import ReceivingPanel, { newIdempotencyKey } from './procurement/ReceivingPanel';
import ProcSection from './procurement/ProcSection';
import { useFieldErrors, useFormSaveEvent, useClearErrorsOnEdit } from './forms/_shared';
import { ORDER_STATUS_LABELS, ORDER_STEPS, PAYMENT_LABELS } from './procurement/procurementLabels';
import { useLoad } from '../hooks/useLoad';
import { useMoney } from '../hooks/useMoney';
import { useRole } from '../hooks/useRole';
import { useShippingAgents } from '../hooks/useShippingAgents';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { PROC_EVENTS, setBarDetail } from '../hooks/useProcurementBar';
import {
  addOrderLine, addParcel, archiveOrder, attachShipment, createOrder, deleteOrderLine, detachShipment, fetchOrder,
  restoreOrder, updateOrder, updateOrderLine,
} from '../services/ProcurementService';
import { CURRENCY_OPTIONS, isCurrencySet } from '../utils/currency';
import { formatDecimal } from '../utils/decimal';
import { entityPath } from '../utils/entityRoutes';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { procurementErrorMessage, toSaveError, validationError } from '../utils/procurementErrors';
import type { LineInput, LineRow, OrderDetail, OrderInput, OrderStatus } from '../types/Procurement';
import '../styles/components/ProcurementScreens.css';

export interface OrderForm {
  marketplace: string;
  marketplace_order_id: string;
  seller_name: string;
  order_date: string;
  currency: string;
  shipping_agent_id: string;
  shipping_agent_office: string;
  estimated_cbm: string;
  actual_cbm: string;
  notes: string;
  total_amount: string;
  discount: string;
  fx_handling_fee: string;
  tax_pct: string;
  tax_amount: string;
  contingency_pct: string;
  fx_rate: string;
  fx_rate_date: string;
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

export const emptyOrderForm = (currency = ''): OrderForm => ({
  marketplace: '', marketplace_order_id: '', seller_name: '', order_date: '', currency, shipping_agent_id: '', shipping_agent_office: '',
  estimated_cbm: '', actual_cbm: '', notes: '', total_amount: '', discount: '', fx_handling_fee: '', tax_pct: '', tax_amount: '',
  contingency_pct: '', fx_rate: '', fx_rate_date: '',
});

export const formFromOrder = (o: OrderDetail): OrderForm => ({
  marketplace: str(o.marketplace), marketplace_order_id: str(o.marketplace_order_id), seller_name: str(o.seller_name),
  order_date: str(o.order_date).slice(0, 10), currency: str(o.currency), shipping_agent_id: str(o.shipping_agent_id),
  shipping_agent_office: str(o.shipping_agent_office), estimated_cbm: str(o.estimated_cbm), actual_cbm: str(o.actual_cbm), notes: str(o.notes),
  total_amount: str(o.total_amount), discount: str(o.discount), fx_handling_fee: str(o.fx_handling_fee), tax_pct: str(o.tax_pct),
  tax_amount: str(o.tax_amount), contingency_pct: str(o.contingency_pct), fx_rate: str(o.fx_rate), fx_rate_date: str(o.fx_rate_date).slice(0, 10),
});

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/**
 * The body to send. Money fields are only included for someone who may see
 * money: a contractor reads them as null, and sending that null back would
 * wipe the real amounts. `payment_status` is never sent (it is derived).
 * Currency is sent on create only.
 */
export function buildOrderPayload(form: OrderForm, opts: { isNew: boolean; canViewFinancials: boolean }): OrderInput {
  const body: OrderInput = {
    marketplace: orNull(form.marketplace),
    marketplace_order_id: orNull(form.marketplace_order_id),
    seller_name: orNull(form.seller_name),
    order_date: orNull(form.order_date),
    shipping_agent_id: orNull(form.shipping_agent_id),
    shipping_agent_office: orNull(form.shipping_agent_office),
    estimated_cbm: orNull(form.estimated_cbm),
    actual_cbm: orNull(form.actual_cbm),
    notes: orNull(form.notes),
  };
  if (opts.isNew) body.currency = form.currency.trim().toUpperCase();
  if (opts.canViewFinancials) {
    body.total_amount = orNull(form.total_amount);
    body.discount = orNull(form.discount);
    body.fx_handling_fee = orNull(form.fx_handling_fee);
    body.tax_pct = orNull(form.tax_pct);
    body.tax_amount = orNull(form.tax_amount);
    body.contingency_pct = orNull(form.contingency_pct);
    body.fx_rate = orNull(form.fx_rate);
    body.fx_rate_date = orNull(form.fx_rate_date);
  }
  return body;
}

const DECIMAL = /^-?\d*\.?\d+$|^-?\d+\.?$/;

/** Field-level problems found before sending. */
export function validateOrderForm(form: OrderForm, opts: { isNew: boolean; canViewFinancials: boolean }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (opts.isNew && !/^[A-Za-z]{3}$/.test(form.currency.trim())) errors.currency = 'Choose a currency: every order needs one.';
  if (opts.canViewFinancials) {
    for (const key of ['total_amount', 'discount', 'fx_handling_fee', 'tax_pct', 'tax_amount', 'contingency_pct', 'fx_rate'] as const) {
      const v = form[key].trim();
      if (v !== '' && !DECIMAL.test(v)) errors[key] = 'Enter a number, for example 1250.50.';
    }
  }
  return errors;
}

let draftCounter = 0;
const draftLine = (input: LineInput, currency: string): LineRow => ({
  id: `draft-${++draftCounter}`, order_id: '', product_name: input.product_name, quantity: input.quantity,
  unit_price: input.unit_price ?? null, currency, line_total: null, pack_size: input.pack_size ?? null, pack_unit: input.pack_unit ?? null,
  quantity_received: 0, image_url: null, destination_type: input.destination_type ?? null, dimensions_cm: input.dimensions_cm ?? null,
  budget_category_id: null, notes: input.notes ?? null,
});

const ProcurementOrderEdit: React.FC<{ orderId?: string }> = ({ orderId }) => {
  const isNew = !orderId;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showSuccess, showError } = useNotifications();
  const { canEdit, canViewFinancials } = useRole();
  const money = useMoney();
  const agents = useShippingAgents();

  const load = useLoad<OrderDetail | null>(() => (orderId ? fetchOrder(orderId) : Promise.resolve(null)), [orderId]);
  const order = load.data;
  const [form, setForm] = useState<OrderForm>(() => emptyOrderForm(money.currency));
  const fieldErrors = useFieldErrors();
  const [draftLines, setDraftLines] = useState<LineRow[]>([]);
  const idempotencyKey = useRef(newIdempotencyKey());
  const receivingRef = useRef<HTMLElement>(null);

  // A new order starts in the project currency once settings have loaded.
  useEffect(() => {
    if (isNew && money.currency) setForm(f => (f.currency ? f : { ...f, currency: money.currency }));
  }, [isNew, money.currency]);

  useEffect(() => {
    if (order) setForm(formFromOrder(order));
  }, [order]);

  useEffect(() => {
    if (!order) { setBarDetail(null); return undefined; }
    setBarDetail({ kind: 'order', id: order.id, title: order.marketplace_order_id || order.seller_name || 'Order', archived: !!order.archived_at });
    return () => setBarDetail(null);
  }, [order]);

  const set = (key: keyof OrderForm) => (e: { target: { value: string } }) => setForm(f => ({ ...f, [key]: e.target.value }));

  const run = async (action: () => Promise<unknown>, done: string | null, failed: string): Promise<boolean> => {
    try {
      await action();
      if (done) showSuccess(done);
      return true;
    } catch (err) {
      showError(procurementErrorMessage(err, failed));
      return false;
    }
  };

  // A failed save throws: useFormSaveEvent then shows the field errors under their fields (or a toast).
  const save = useCallback(async () => {
    if (!canEdit) return;
    const found = validateOrderForm(form, { isNew, canViewFinancials });
    if (Object.keys(found).length > 0) throw validationError(found);
    try {
      const body = buildOrderPayload(form, { isNew, canViewFinancials });
      if (isNew) {
        const items: LineInput[] = draftLines.map(l => ({
          product_name: l.product_name, quantity: l.quantity, pack_size: l.pack_size, pack_unit: l.pack_unit,
          destination_type: l.destination_type, dimensions_cm: l.dimensions_cm, notes: l.notes,
          ...(canViewFinancials ? { unit_price: l.unit_price } : {}),
        }));
        const created = await createOrder({ ...body, ...(items.length ? { items } : {}), idempotency_key: idempotencyKey.current });
        showSuccess('Order created');
        navigate(entityPath('procurementOrder', created.id) ?? '/procurement/orders', { replace: true });
      } else {
        await updateOrder(orderId as string, body);
        showSuccess('Order saved');
        await load.reload();
      }
    } catch (err) {
      throw toSaveError(err, 'The order could not be saved. Please try again.');
    }
  }, [canEdit, form, isNew, canViewFinancials, draftLines, orderId, navigate, load, showSuccess]);

  // The breadcrumb bar's Save sends PROC_EVENTS.orderSave.
  useFormSaveEvent(save, { event: PROC_EVENTS.orderSave, fieldErrors, fallback: 'The order could not be saved. Please try again.' });
  useClearErrorsOnEdit({ ...form }, fieldErrors);

  // Unsaved edits: compared with what was loaded (or the blank form on a new order).
  const baseline = useMemo(() => (order ? formFromOrder(order) : emptyOrderForm(money.currency)), [order, money.currency]);
  const dirty = canEdit && (isNew || !!order) && (JSON.stringify(form) !== JSON.stringify(baseline) || (isNew && draftLines.length > 0));
  useUnsavedChangesGuard(dirty);

  const archive = useCallback(async () => {
    if (!orderId) return;
    const ok = await confirm({ title: 'Archive this order?', subject: order?.marketplace_order_id ?? undefined, description: 'It leaves the active list. You can restore it from the Archived view.', confirmLabel: 'Archive', variant: 'danger' });
    if (!ok) return;
    if (await run(() => archiveOrder(orderId), 'Order archived', 'The order could not be archived.')) navigate('/procurement/orders');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, order, confirm, navigate]);

  const restore = useCallback(async () => {
    if (!orderId) return;
    if (await run(() => restoreOrder(orderId), 'Order restored', 'The order could not be restored.')) await load.reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, load]);

  // The breadcrumb bar sends these (Save is wired above through useFormSaveEvent).
  const latest = useRef({ archive, restore });
  latest.current = { archive, restore };
  useEffect(() => {
    const onArchive = () => void latest.current.archive();
    const onRestore = () => void latest.current.restore();
    const onReceive = () => receivingRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    window.addEventListener(PROC_EVENTS.orderArchive, onArchive);
    window.addEventListener(PROC_EVENTS.orderRestore, onRestore);
    window.addEventListener(PROC_EVENTS.orderReceive, onReceive);
    return () => {
      window.removeEventListener(PROC_EVENTS.orderArchive, onArchive);
      window.removeEventListener(PROC_EVENTS.orderRestore, onRestore);
      window.removeEventListener(PROC_EVENTS.orderReceive, onReceive);
    };
  }, []);

  const moveStatus = async (status: string) => {
    if (!orderId || !canEdit || status === order?.status) return;
    if (await run(() => updateOrder(orderId, { status: status as OrderStatus }), `Order moved to ${ORDER_STATUS_LABELS[status as OrderStatus] ?? status}`, 'The status could not be changed.')) await load.reload();
  };

  const cancelOrder = async () => {
    if (!orderId) return;
    const ok = await confirm({ title: 'Cancel this order?', subject: order?.marketplace_order_id ?? undefined, description: 'A delivered order cannot be cancelled.', confirmLabel: 'Cancel order', cancelLabel: 'Keep order', variant: 'danger' });
    if (ok) await moveStatus('cancelled');
  };

  if (load.error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="this order" error={load.error} onRetry={load.reload} retrying={load.loading} /></div>;
  }
  if (!isNew && !order) {
    return <div className="wt-container fade-in"><p className="text-subtle" role="status">Loading order...</p></div>;
  }

  const lines: LineRow[] = isNew ? draftLines : order?.items ?? [];
  const currency = order?.currency ?? (form.currency || null);
  const readOnly = !canEdit;
  const agentOptions = [{ value: '', label: 'No agent' }, ...agents.agents.map(a => ({ value: a.id, label: a.name }))];
  const office = agents.allAgents.find(a => a.id === form.shipping_agent_id)?.offices ?? [];
  const reloadOrder = async () => { await load.reload(); };

  const lineHandlers = isNew ? {
    onAdd: async (input: LineInput) => { setDraftLines(l => [...l, draftLine(input, form.currency)]); },
    onUpdate: async (id: string, input: LineInput) => { setDraftLines(l => l.map(x => (x.id === id ? { ...draftLine(input, form.currency), id } : x))); },
    onRemove: async (line: LineRow) => { setDraftLines(l => l.filter(x => x.id !== line.id)); },
  } : {
    onAdd: async (input: LineInput) => { await addOrderLine(orderId as string, input); await reloadOrder(); },
    onUpdate: async (id: string, input: LineInput) => { await updateOrderLine(orderId as string, id, input); await reloadOrder(); },
    onRemove: async (line: LineRow) => {
      if (await run(() => deleteOrderLine(orderId as string, line.id), 'Line removed', 'The line could not be removed.')) await reloadOrder();
    },
  };

  const err = fieldErrors.errorFor;
  const archived = !!order?.archived_at;

  return (
    <div className="wt-container fade-in proc-detail">
      <div className="proc-title-row">
        <h2 className="heading-page">{isNew ? 'New order' : order?.marketplace_order_id || order?.seller_name || 'Order'}</h2>
        {archived && <StatusBadge variant="neutral">Archived</StatusBadge>}
      </div>

      <div className="detail-layout">
        <div className="inline-form-page">
          {archived && (
            <div className="entity-state-banner entity-state-banner--locked" role="status">
              Archived. Restore it from the header to edit its lines or receive goods.
            </div>
          )}
          {order?.status === 'cancelled' && !archived && (
            <div className="entity-state-banner entity-state-banner--cancelled" role="status">This order was cancelled.</div>
          )}
          {readOnly && (
            <div className="entity-state-banner entity-state-banner--partial" role="status">You can view this order but not change it.</div>
          )}

          {order && (
            <ProcSection title="Status" ariaLabel="Order status">
              <StatusStepper
                steps={ORDER_STEPS}
                current={order.status}
                terminalRejected={['cancelled']}
                onChange={canEdit && !archived ? status => void moveStatus(status) : undefined}
                ariaLabel="Order status"
              />
              {canEdit && order.status !== 'cancelled' && order.status !== 'delivered' && !archived && (
                <div className="proc-section-actions">
                  <Button variant="ghost" size="sm" onClick={() => void cancelOrder()}>Cancel order</Button>
                </div>
              )}
            </ProcSection>
          )}

          <ProcSection title="Order details" ariaLabel="Order details">
            <div className="form-row">
              <FormInput label="Marketplace" value={form.marketplace} onChange={set('marketplace')} error={err('marketplace')} disabled={readOnly} />
              <FormInput label="Order number" value={form.marketplace_order_id} onChange={set('marketplace_order_id')} error={err('marketplace_order_id')} disabled={readOnly} />
              <FormInput label="Seller" value={form.seller_name} onChange={set('seller_name')} error={err('seller_name')} disabled={readOnly} />
            </div>
            <div className="form-row">
              <FormInput label="Order date" type="date" value={form.order_date} onChange={set('order_date')} error={err('order_date')} disabled={readOnly} />
              {isNew ? (
                <FormSelect
                  label="Currency" required value={form.currency} onChange={set('currency')} error={err('currency')}
                  options={[{ value: '', label: 'Choose a currency' }, ...CURRENCY_OPTIONS]}
                  help={isCurrencySet(money.currency) ? undefined : 'The project has no currency yet; choose the one this order is in.'}
                />
              ) : (
                <FormInput label="Currency" value={form.currency} disabled readOnly help="Set when the order was created." />
              )}
            </div>
          </ProcSection>

          <ProcSection title="Shipping and volume" ariaLabel="Shipping and volume">
            <div className="form-row">
              <FormSelect label="Shipping agent" value={form.shipping_agent_id} onChange={set('shipping_agent_id')} error={err('shipping_agent_id')} disabled={readOnly} options={agentOptions} />
              <FormSelect
                label="Pickup office" value={form.shipping_agent_office} onChange={set('shipping_agent_office')} error={err('shipping_agent_office')} disabled={readOnly || office.length === 0}
                options={[{ value: '', label: 'Not set' }, ...office.map(o => ({ value: o.label, label: o.label }))]}
              />
            </div>
            <div className="form-row">
              <FormInput label="Estimated CBM" inputMode="decimal" value={form.estimated_cbm} onChange={set('estimated_cbm')} error={err('estimated_cbm')} disabled={readOnly} />
              <FormInput label="Actual CBM" inputMode="decimal" value={form.actual_cbm} onChange={set('actual_cbm')} error={err('actual_cbm')} disabled={readOnly} />
            </div>
            {agents.error ? <LoadErrorBox what="shipping agents" error={agents.error} onRetry={agents.reload} retrying={agents.loading} /> : null}
          </ProcSection>

          {canViewFinancials && (
            <ProcSection title={`Amounts${currency ? ` (${currency})` : ''}`} ariaLabel="Order amounts">
              <div className="form-row">
                <FormInput label="Goods total" inputMode="decimal" value={form.total_amount} onChange={set('total_amount')} error={err('total_amount')} disabled={readOnly} />
                <FormInput label="Discount" inputMode="decimal" value={form.discount} onChange={set('discount')} error={err('discount')} disabled={readOnly} />
                <FormInput label="Currency handling fee" inputMode="decimal" value={form.fx_handling_fee} onChange={set('fx_handling_fee')} error={err('fx_handling_fee')} disabled={readOnly} />
              </div>
              <div className="form-row">
                <FormInput label="Tax (%)" inputMode="decimal" value={form.tax_pct} onChange={set('tax_pct')} error={err('tax_pct')} disabled={readOnly} />
                <FormInput label="Tax (amount)" inputMode="decimal" value={form.tax_amount} onChange={set('tax_amount')} error={err('tax_amount')} disabled={readOnly} />
                <FormInput label="Contingency (%)" inputMode="decimal" value={form.contingency_pct} onChange={set('contingency_pct')} error={err('contingency_pct')} disabled={readOnly} />
              </div>
              <div className="form-row">
                <FormInput label={`Exchange rate to project currency${money.currency ? ` (${money.currency})` : ''}`} inputMode="decimal" value={form.fx_rate} onChange={set('fx_rate')} error={err('fx_rate')} disabled={readOnly} />
                <FormInput label="Rate date" type="date" value={form.fx_rate_date} onChange={set('fx_rate_date')} error={err('fx_rate_date')} disabled={readOnly} />
              </div>
              {order && (
                <dl className="proc-kv">
                  <dt>Payment</dt><dd>{order.payment_status ? PAYMENT_LABELS[order.payment_status] : '—'}</dd>
                  <dt>Paid so far</dt><dd>{formatDecimal(order.paid, order.currency)}</dd>
                  <dt>Estimated landed cost</dt><dd>{formatDecimal(order.landing_cost, money.currency || undefined)}</dd>
                  <dt>Actual landed cost</dt><dd>{formatDecimal(order.actual_landing_cost, money.currency || undefined)}</dd>
                </dl>
              )}
            </ProcSection>
          )}

          <ProcSection title="Notes" ariaLabel="Order notes">
            <FormTextarea label="Notes" rows={3} value={form.notes} onChange={set('notes')} error={err('notes')} disabled={readOnly} />
          </ProcSection>

          <OrderLines lines={lines} currency={currency} canEdit={canEdit && !archived} canViewFinancials={canViewFinancials} {...lineHandlers} />

          {order && (
            <>
              <OrderParcels
                parcels={order.tracking}
                canEdit={canEdit}
                onAdd={async input => { await addParcel(order.id, input); await reloadOrder(); }}
              />
              <OrderShipments
                shipments={order.shipments}
                canEdit={canEdit}
                onAttach={async (shipmentId, input) => { await attachShipment(order.id, shipmentId, input); await reloadOrder(); }}
                onDetach={async shipment => { if (await run(() => detachShipment(order.id, shipment.id), 'Shipment unlinked', 'The shipment could not be unlinked.')) await reloadOrder(); }}
              />
              <ReceivingPanel ref={receivingRef} orderId={order.id} canEdit={canEdit && !archived} onReceived={() => void reloadOrder()} />
              {canViewFinancials && <LandedCostPanel cost={order.landed_cost} />}
              <ProcSection title="History" ariaLabel="History">
                {order.history.length === 0 ? <p className="text-subtle">No status changes yet.</p> : (
                  <ul className="proc-history">
                    {order.history.map((h, i) => (
                      <li key={`${h.changed_at}-${i}`}>
                        <strong>{ORDER_STATUS_LABELS[h.to_status as OrderStatus] ?? h.to_status}</strong>
                        {h.from_status ? ` (from ${ORDER_STATUS_LABELS[h.from_status as OrderStatus] ?? h.from_status})` : ''}
                        <span className="text-subtle"> {formatDateTime(h.changed_at) || formatDate(h.changed_at, '')}{h.actor ? `, ${h.actor}` : ''}{h.notes ? `. ${h.notes}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </ProcSection>
            </>
          )}
        </div>

        <aside className="detail-sidebar">
          <section className="side-section">
            <h4>Order Summary</h4>
            <div className="stat-row"><span>Status</span><strong>{order ? ORDER_STATUS_LABELS[order.status] ?? order.status : 'New'}</strong></div>
            <div className="stat-row"><span>Lines</span><strong>{lines.length}</strong></div>
            <div className="stat-row"><span>Shipments</span><strong>{order?.shipments.length ?? 0}</strong></div>
            {canViewFinancials && order && (
              <div className="stat-row"><span>Paid so far</span><strong>{formatDecimal(order.paid, order.currency)}</strong></div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
};

export default ProcurementOrderEdit;
